import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { Contact } from '../shared/models/conflict-result';
import { MatDialog } from '@angular/material/dialog';
import { VerificationResultComponent } from '../verification-result/verification-result.component';
import { IdVerificationService } from '../shared/services/id-verification.service';
import { firstValueFrom } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Jurisdiction } from '../shared/models/jurisdiction';
import { faFilePdf } from '@fortawesome/free-regular-svg-icons';
import { IdVerificationResponse } from '../shared/models/id-verification-response';
import { DocumentListComponent } from '../document-list/document-list.component';
import { ActionDocuments } from '../shared/models/action-documents';
import { SnackbarService } from '../shared/services/snackbar.service';
import { CountryFieldRequirementService } from '../shared/services/country-field-requirement.service';
import { CountryFieldValidationService } from '../shared/services/country-field-validation.service';
import { CitizenshipCode, CountryFieldRequirement } from '../shared/models/country-field-requirement';

@Component({
    selector: 'xp-contact-result',
    templateUrl: './contact-result.component.html',
    styleUrl: './contact-result.component.scss'
})
export class ContactResultComponent implements OnInit {
    faFilePdf = faFilePdf;
    readonly dialog = inject(MatDialog);
    isIndividualOrCompany: string | null = "";
    showProgressBar: boolean = true;
    contacts: Contact[] = [];
    verificationId: number | null = null;
    verificationResponse?: IdVerificationResponse;
    isVerifyDisabled: boolean = false;
    jurisdictions: Jurisdiction[] = [];
    selectedJurisdiction: string = '';
    defaultSelectedIndex: number = 0;
    selectedIndex: number = this.defaultSelectedIndex;
    contactsPageCount: number = 1;
    pageSize: number = 10;
    selectedContacts: Contact[] = [];
    selectedDocumentsButton: { [contactId: number]: number } = {};
    countryConfigMap: Map<string, CountryFieldRequirement> = new Map();
    citizenshipCodes: CitizenshipCode[] = [];
    enforcementEnabled: boolean = true;

    constructor(
        private idVerificationService: IdVerificationService,
        private snackBar: SnackbarService,
        private cdr: ChangeDetectorRef,
        private countryFieldRequirementService: CountryFieldRequirementService,
        private countryFieldValidationService: CountryFieldValidationService) {
    }

    async ngOnInit() {
        this.showProgressBar = true;
        this.resetData();
        this.verificationId = null;

        const config = await firstValueFrom(this.countryFieldRequirementService.getConfig());
        this.countryConfigMap = new Map(config.countries.map(c => [c.countryCode.toUpperCase(), c]));
        this.citizenshipCodes = config.citizenshipCodes;
        this.enforcementEnabled = config.enforcementEnabled;

        this.iDVSearch(1);
    }

    iDVSearch(pageNumber: number, iDVId: number | null = null) {
        const matterNo = Number(sessionStorage.getItem("matterId"));

        if (!matterNo) {
            this.showProgressBar = false;
            this.snackBar.errorSnackBar("Matter number not found!");
            return;
        }

        this.idVerificationService.searchContact(matterNo).subscribe({
            next: (result: Contact[]) => {
                this.contacts = result;

                this.contacts = result.map(c => ({
                    ...c,
                    dateOfBirth: c.isCompany ? null : c.dateOfBirth
                }));

                const invalidContacts = this.contacts
                    .map(c => ({ contact: c, result: this.countryFieldValidationService.validate(c, this.countryConfigMap, this.enforcementEnabled, this.citizenshipCodes) }))
                    .filter(x => !x.result.isValid);

                if (invalidContacts.length > 0) {
                    const list = invalidContacts
                        .map(x => `${x.contact.contactId}:${x.contact.contactName} (${x.result.failures.map(f => f.message).join(', ')})`)
                        .join('; ');

                    this.snackBar.errorSnackBarDOB(
                        `The following contacts are missing required information: ${list}. Please update ActionStep.`
                    );
                }

                if (result.length === 1) {
                    const singleContactId = result[0].contactId;
                    this.include(singleContactId);
                    this.changeIsPrimary(singleContactId);
                }

                this.showProgressBar = false;
            },
            error: (err) => {
                const message = err?.error?.errors?.[0]?.message || "Something went wrong!";
                this.snackBar.errorSnackBar(message);
                this.showProgressBar = false;
            }
        });
    }
    hasIndividual(): boolean {
        return this.contacts?.some(c => !c.isCompany) ?? false;
    }


    resetData() {
        this.contacts = [];
    }

    async idVerify() {
        var orgKey = sessionStorage.getItem("ORG_KEY");

        if (orgKey != null || orgKey != "undefined") {
            var verify = true;
            if (this.selectedContacts.length == 0) {
                verify = false;
                this.snackBar.warningSnackBar(`Please select contacts.`);
            }
            else if (this.selectedContacts.findIndex(c => !c.isCompany && c.role == null) != -1) {
                verify = false;
                this.snackBar.warningSnackBar(`Please select 'Role' for the selected ${this.selectedContacts.length == 1 ? 'contact' : 'contacts'}.`);
            }
            else {
                this.selectedContacts.forEach(contact => {
                    if (!this.checkValidations(contact)) {
                        verify = false
                    }
                })
            }

            if (this.selectedContacts.length > 0 && verify) {
                this.isVerifyDisabled = true;
                this.showProgressBar = true;

                await firstValueFrom(this.idVerificationService.runStrategy(this.selectedContacts)).then(res => {
                    this.verificationResponse = res,
                        this.isVerifyDisabled = false,
                        this.showProgressBar = false;
                    var contactdata = this.dialog.open(VerificationResultComponent, {
                        data: this.verificationResponse,
                        height: '700px',
                        maxHeight: '700px',
                        width: '1400px',
                        maxWidth: '1400px',
                        panelClass: 'full-screen-modal',
                        disableClose: true  
                    });
                    contactdata.afterClosed().subscribe((returnValues: ActionDocuments[]) => {

                        this.selectedContacts = [];
                        this.iDVSearch(1);

                    });
                }
                ).catch(err => {
                    this.snackBar.errorSnackBar(err.error.errors[0].message);
                    this.isVerifyDisabled = false;
                    this.showProgressBar = false;
                }
                );
            }
        }
    }

    createAddress(contact: Contact) {
        var address = [contact.houseName, contact.houseNumber, contact.street, contact.city, contact.cityDistrict, contact.state, contact.country, contact.postcode];
        return address.filter(c => c && c.length > 0).join(", ");
    }

    configFor(contact: Contact): CountryFieldRequirement | null {
        return this.countryConfigMap.get((contact.country ?? '').toUpperCase()) ?? null;
    }

    checkValidations(selectedContact: Contact) {
        if (this.selectedContacts.findIndex(c => c.isPrimary) == -1) {
            this.snackBar.warningSnackBar(`Please select one 'Primary Contact'`);
            return false;
        }
        else {
            const result = this.countryFieldValidationService.validate(selectedContact, this.countryConfigMap, this.enforcementEnabled, this.citizenshipCodes);
            if (result.isValid) {
                return true;
            }
            else {
                const messages = result.failures.map(f => f.message).join('; ');
                this.snackBar.errorSnackBarDOB(`Please provide the following for contact '${selectedContact.contactId}:${selectedContact.contactName}' in ActionStep: ${messages}`);
                return false;
            }
        }
    }

    include(contactId: number) {
        if (this.selectedContacts.findIndex(c => c.contactId == contactId) == -1) {
            this.selectedContacts.push(this.contacts.find(c => c.contactId == contactId)!);
        }
        else {
            this.selectedContacts = this.selectedContacts.filter(c => c.contactId != contactId);
            var contactIndex = this.contacts.findIndex(c => c.contactId == contactId)!;
            this.contacts[contactIndex].isPrimary = false;
        }

    }

    isIncluded(contactId: number) {
        if (this.selectedContacts.findIndex(c => c.contactId == contactId) != -1) {
            return true;
        }
        return false;
    }

    changeIsPrimary(contactId: number) {
        if (this.selectedContacts.findIndex(c => c.contactId == contactId) == -1) {
            var contactIndex = this.contacts.findIndex(c => c.contactId == contactId)!;
            this.contacts[contactIndex].isPrimary = false;
        } else {
            this.selectedContacts = this.selectedContacts.map(contact => {
                var contactIndex = this.contacts.findIndex(c => c.contactId == contact.contactId)!;
                if (contact.contactId == contactId) {
                    contact.isPrimary = !contact.isPrimary;
                }
                else {
                    contact.isPrimary = false;
                }

                this.contacts[contactIndex].isPrimary = contact.isPrimary
                return contact;
            });
        }
    }

    checkBiometric(data: Contact) {
        data.IsBiometric = !data.IsBiometric;
    }

    disableCheckbox(contactId: number) {
        return this.selectedContacts.findIndex(c => c.contactId == contactId) == -1
    }

    trackByContactId(index: number, contact: Contact): number {
        return contact.contactId;
    }

    removeDocument(contactId: number, doc: any): void {
        const contact = this.contacts.find(c => c.contactId === contactId);
        if (contact) {
            // Remove the document from the list
            contact.selectedDocuments = contact.selectedDocuments?.filter(d => d !== doc);
            contact.selectedDocuments = contact.selectedDocuments?.filter(d => d !== doc);

            // Reset button state if no documents are left
            if (!contact.selectedDocuments?.length) {
                this.selectedDocumentsButton[contactId] = 0;
            }
            else if (!contact.selectedDocuments?.length) {
                this.selectedDocumentsButton[contactId] = 0;
            }
            this.cdr.detectChanges();
        }
    }

    onButtonClick(contactId: number, buttonId: number): void {
        const matterNo = sessionStorage.getItem("matterId");
        // Fetch already selected documents for the contact
        const contact = this.contacts.find(c => c.contactId === contactId);
        const selectedDocs = contact?.selectedDocuments || [];
        const documentType = buttonId === 1 ? 'POI' : 'POA';

        const dialogRef = this.dialog.open(DocumentListComponent, {
            maxWidth: '60vw',
            width: '90%',
            panelClass: 'responsive-dialog',
            data: { actionId: matterNo, selectedDocuments: selectedDocs, documentType: documentType, country: contact?.country },
            disableClose: true
        });

        dialogRef.afterClosed().subscribe((returnValues: ActionDocuments[]) => {
            if (returnValues) {
                // Find the contact by contactId
                const contact = this.contacts.find(c => c.contactId === contactId);

                if (contact) {
                    this.selectedDocumentsButton[contactId] = this.selectedDocumentsButton[contactId] || 0;
                    this.selectedDocumentsButton[contactId] = (this.selectedDocumentsButton[contactId] % 2) + 1;
                    // Update selected documents based on the button clicked
                    if (buttonId === 1) {
                        contact.selectedDocuments = contact.selectedDocuments || [];
                        // Assign documentType as POI
                        returnValues.forEach(doc => doc.documentType = 'POI');
                        contact.selectedDocuments = [...contact.selectedDocuments, ...returnValues];

                    }
                    else if (buttonId === 2) {
                        contact.selectedDocuments = contact.selectedDocuments || [];
                        // Assign documentType as POA
                        returnValues.forEach(doc => doc.documentType = 'POA');
                        contact.selectedDocuments = [...contact.selectedDocuments, ...returnValues];
                    }
                }
            }
        });
    }

    getdocumentsbytype(documents: ActionDocuments[], type: string) {
        return documents.filter(d => d.documentType == type);
    }

    verifybtn() {
        // Check if all selected contacts meet the criteria
        const filteredContacts = this.selectedContacts.filter(contact => {
            if (contact.isCompany) {
                // Company contacts must have a primary set
                return contact.isPrimary !== null && contact.isPrimary !== undefined;
            } else {
                // Individual contacts must have a role and primary set
                return contact.role !== null && contact.isPrimary !== null && contact.isPrimary !== undefined;
            }
        });

        // Return true if all selected contacts meet the criteria, otherwise false
        return (
            this.selectedContacts.length > 0 &&
            filteredContacts.length > 0 &&
            filteredContacts.length === this.selectedContacts.length
        );
    }

}

